/**
 * How the per-simulation sim-chat cap is shown, and — just as importantly — how it is NOT shown.
 *
 * THE ORIGINAL COMPLAINT: a summit account read "Sim Chats 0 / ∞" while the server enforced five questions per
 * simulation. PR #843 had set `simulation_chat_requests` to -1 and moved the real cap to
 * `simulationChatsPerSimulationLimit`, so the one number the user could not exceed was the one number not on
 * screen.
 *
 * MY FIRST FIX WAS WRONG, and the review caught it. It rendered the per-simulation cap as the DENOMINATOR of
 * the existing progress row while leaving `simulationChatsUsed` as the numerator — and that counter is
 * MONTHLY. A summit account with twelve sim-chat messages this month would have read "12 / 5", bar maxed and
 * red, while every individual simulation sat at one or two of its own five. That is the same
 * number-on-screen-disagrees-with-what-is-enforced bug, moved from the denominator to the numerator.
 *
 * THE FIX: do not pair them at all. The progress row keeps the monthly pair it has always shown, because that
 * is what the numerator counts. The per-simulation cap goes to the STATIC LIMITS line beside
 * "N questions/survey" — a plan limit with no progress bar and no numerator to disagree with. It is a static
 * fact about the plan, and the panel already had a home for exactly that.
 */

/** The API's spelling of "no limit". */
export const UNLIMITED = -1;

export interface SimChatUsage {
  simulationChatsUsed: number;
  simulationChatsLimit: number;
  /** Absent on older responses, so treated as "no per-simulation cap" rather than as zero. */
  simulationChatsPerSimulationLimit?: number;
}

/**
 * The static-limits phrase for the per-simulation cap, or null when the plan has none.
 *
 * Returns a phrase rather than a number so the call site cannot accidentally pair it with a counter — the
 * mistake this module exists to have stopped making.
 */
export function perSimulationCapLabel(usage: SimChatUsage): string | null {
  const cap = usage.simulationChatsPerSimulationLimit;
  if (typeof cap !== 'number' || cap <= 0 || cap === UNLIMITED) return null;
  return `${cap} chats/simulation`;
}

/** The monthly sim-chat row: the pair that has always matched, unchanged. */
export function monthlySimChats(usage: SimChatUsage): {
  current: number;
  limit: number;
  isUnlimited: boolean;
} {
  return {
    current: usage.simulationChatsUsed,
    limit: usage.simulationChatsLimit,
    isUnlimited: usage.simulationChatsLimit === UNLIMITED,
  };
}
